import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Flex,
  List,
  Pagination,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography
} from "antd";
import {
  EyeInvisibleOutlined,
  FilterOutlined,
  ReloadOutlined,
  SettingOutlined,
  StarOutlined,
  UndoOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { getMeta, getProfile, getStatus, listLeads, setLeadStatus, startSync } from "./api";
import {
  SOURCE_SHORT,
  TRADE_LABELS,
  daysLeft,
  formatDate,
  formatDistance,
  formatEuro
} from "./constants";
import ScoreBadge from "./ScoreBadge";
import SearchProfileModal from "./SearchProfileModal";
import LeadDetailDrawer from "./LeadDetailDrawer";
import PrepareOfferModal from "./PrepareOfferModal";
import "./auftragssuche.css";

const PAGE_SIZE = 20;
const POLL_MS = 4000;

const Auftragssuche = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [meta, setMeta] = useState(null);
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [tab, setTab] = useState("new");
  const [filters, setFilters] = useState({
    searchText: "",
    source: undefined,
    clientType: undefined,
    sort: "score",
    withoutLocation: false
  });
  const [pageNo, setPageNo] = useState(1);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [counts, setCounts] = useState({});
  const [withoutLocationCount, setWithoutLocationCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailKey, setDetailKey] = useState(null);
  const [offerLead, setOfferLead] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const pollTimer = useRef(null);
  const isMobile = useIsMobile();

  const ready = !!(profile && !profile.isNew && (profile.trades || []).length);

  const loadStatus = async () => {
    const res = await getStatus();
    setStatus(res.data);
    return res.data;
  };

  const loadLeads = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const res = await listLeads({ tab, ...filters, pageNo: pageNo - 1, pageSize: PAGE_SIZE });
      setData(res.data.data);
      setCount(res.data.count);
      setCounts(res.data.counts);
      setWithoutLocationCount(res.data.withoutLocationCount || 0);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  // While a run is in progress, poll until it ends, then refresh the list.
  const pollWhileRunning = () => {
    clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(async () => {
      try {
        const s = await loadStatus();
        if (s.running) pollWhileRunning();
        else loadLeads();
      } catch (err) {
        generalErrorAlert(err);
      }
    }, POLL_MS);
  };

  useEffect(() => {
    Promise.all([getMeta(), getProfile(), getStatus()])
      .then(([m, p, s]) => {
        setMeta(m.data);
        setProfile(p.data);
        setStatus(s.data);
        if (p.data.isNew) setProfileOpen(true);
        if (s.data.running) pollWhileRunning();
      })
      .catch(generalErrorAlert);
    return () => clearTimeout(pollTimer.current);
  }, []);

  useEffect(() => {
    loadLeads();
  }, [ready, tab, filters, pageNo]);

  const updateFilter = patch => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPageNo(1);
  };

  // Filters that differ from the defaults; shown as a count on the phone "Filter" button.
  const activeFilterCount =
    (filters.source ? 1 : 0) + (filters.clientType ? 1 : 0) + (filters.sort !== "score" ? 1 : 0);

  const resetFilters = () =>
    updateFilter({ source: undefined, clientType: undefined, sort: "score" });

  const handleSync = async () => {
    try {
      const res = await startSync();
      if (!res.data.started && !res.data.running)
        Success(t("The sources were checked a few minutes ago."));
      await loadStatus();
      pollWhileRunning();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const handleProfileSaved = saved => {
    const firstSetup = !ready;
    setProfile(saved);
    setProfileOpen(false);
    if (firstSetup) handleSync();
    else loadLeads();
  };

  const quickStatus = async (key, next) => {
    try {
      await setLeadStatus(key, { status: next });
      loadLeads();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const enabled = profile?.sources
    ? Object.keys(profile.sources).filter(id => profile.sources[id])
    : [];
  const sourceStates = (status?.sources || []).filter(s => enabled.includes(s.id));

  const renderStatusLine = () => {
    if (status?.running) {
      return <Tag color="processing">{t("Searching the sources…")}</Tag>;
    }
    return (
      <Space wrap size={[4, 4]}>
        {sourceStates.map(s => (
          <Tooltip
            key={s.id}
            title={
              s.lastError
                ? `${t("Last run failed")}: ${s.lastError}`
                : s.lastSuccessAt
                  ? `${t("Last checked")}: ${formatDate(s.lastSuccessAt, lng, true)} · ${t("{{count}} new or updated", { count: s.lastStored })}`
                  : t("Not checked yet")
            }
          >
            <Tag color={s.lastError ? "error" : s.lastSuccessAt ? "success" : "default"}>
              {SOURCE_SHORT[s.id]} {s.lastSuccessAt ? formatDate(s.lastSuccessAt, lng, true) : "–"}
            </Tag>
          </Tooltip>
        ))}
      </Space>
    );
  };

  const renderItem = lead => {
    const left = daysLeft(lead.submissionDeadline);
    const status = lead.lead?.status;
    return (
      <List.Item className="auftrag-item" key={lead.key}>
        <Row gutter={[16, 12]} wrap={isMobile} className="auftrag-item-row" align="top">
          <Col flex="none">
            <ScoreBadge fit={lead.fit} size={isMobile ? 44 : 52} />
          </Col>
          <Col flex="1 1 0" className="auftrag-item-body">
            <Typography.Link
              strong
              className="auftrag-title"
              onClick={() => setDetailKey(lead.key)}
            >
              {lead.title}
            </Typography.Link>
            <div className="auftrag-meta">
              <span>{lead.buyerName || t("Private customer")}</span>
              {lead.locationText && (
                <span>
                  {lead.locationText}
                  {lead.distanceKm !== null &&
                    !lead.locationFromBuyer &&
                    ` · ${formatDistance(lead)}`}
                </span>
              )}
              {lead.submissionDeadline && (
                <span>
                  {t("Deadline")} {formatDate(lead.submissionDeadline, lng)}{" "}
                  <Tag color={left < 7 ? "red" : left < 14 ? "orange" : "green"}>
                    {t("{{count}} days left", { count: Math.max(left, 0) })}
                  </Tag>
                </span>
              )}
              {lead.estimatedValueEur !== null && (
                <span>{formatEuro(lead.estimatedValueEur, lng)}</span>
              )}
            </div>
            {lead.description && (
              <Typography.Paragraph
                type="secondary"
                ellipsis={{ rows: 2 }}
                className="auftrag-desc"
              >
                {lead.description}
              </Typography.Paragraph>
            )}
            <Space wrap size={[4, 4]}>
              <Tag color={lead.clientType === "PRIVATE" ? "purple" : "blue"}>
                {SOURCE_SHORT[lead.sourceId]}
              </Tag>
              {lead.trades.map(tr => (
                <Tag key={tr}>{t(TRADE_LABELS[tr] || tr)}</Tag>
              ))}
              {lead.lead?.offer && <Tag color="green">{t("Offer created")}</Tag>}
            </Space>
          </Col>
          <Col flex={isMobile ? "1 1 100%" : "none"} className="auftrag-actions">
            {!lead.lead?.offer && (
              <Button
                type="primary"
                className="auftrag-offer-btn"
                onClick={() => setOfferLead(lead)}
              >
                {t("Prepare offer")}
              </Button>
            )}
            <div className="auftrag-quick-actions">
              {!status && (
                <Tooltip title={t("Save")}>
                  <Button
                    icon={<StarOutlined />}
                    aria-label={t("Save")}
                    onClick={() => quickStatus(lead.key, "SAVED")}
                  />
                </Tooltip>
              )}
              {!status && (
                <Tooltip title={t("Hide")}>
                  <Button
                    icon={<EyeInvisibleOutlined />}
                    aria-label={t("Hide")}
                    onClick={() => quickStatus(lead.key, "DISMISSED")}
                  />
                </Tooltip>
              )}
              {status && status !== "OFFER_DRAFTED" && (
                <Tooltip title={t("Back to new matches")}>
                  <Button
                    icon={<UndoOutlined />}
                    aria-label={t("Back to new matches")}
                    onClick={() => quickStatus(lead.key, null)}
                  />
                </Tooltip>
              )}
            </div>
          </Col>
        </Row>
      </List.Item>
    );
  };

  const sourceSelect = (
    <Select
      allowClear
      className="auftrag-filter-select"
      placeholder={t("All sources")}
      value={filters.source}
      onChange={v => updateFilter({ source: v })}
      options={enabled.map(id => ({ value: id, label: SOURCE_SHORT[id] }))}
    />
  );
  const clientSelect = (
    <Select
      allowClear
      className="auftrag-filter-select"
      placeholder={t("All clients")}
      value={filters.clientType}
      onChange={v => updateFilter({ clientType: v })}
      options={[
        { value: "PUBLIC_BUYER", label: t("Public tender") },
        { value: "PRIVATE", label: t("Private customer") }
      ]}
    />
  );
  const sortSelect = (
    <Select
      className="auftrag-filter-select"
      value={filters.sort}
      onChange={v => updateFilter({ sort: v })}
      options={[
        { value: "score", label: t("Best fit") },
        { value: "deadline", label: t("Deadline") },
        { value: "newest", label: t("Newest") },
        { value: "distance", label: t("Distance") }
      ]}
    />
  );
  const searchBar = <SearchBarComp handleSearch={v => updateFilter({ searchText: v })} />;

  const tabs = (
    <Segmented
      className="auftrag-tabs"
      value={tab}
      onChange={v => {
        setTab(v);
        setPageNo(1);
      }}
      options={[
        { value: "new", label: t("New matches") },
        { value: "saved", label: `${t("Saved")} (${counts.saved || 0})` },
        { value: "drafted", label: `${t("Offer created")} (${counts.drafted || 0})` },
        { value: "dismissed", label: `${t("Hidden")} (${counts.dismissed || 0})` }
      ]}
    />
  );

  // Phones: search stays visible, the selects fold into a "Filter" panel.
  const mobileFilters = (
    <div className="auftrag-filterbar">
      {tabs}
      <div className="auftrag-filterbar-search">
        {searchBar}
        <Badge count={activeFilterCount} size="small">
          <Button
            size="large"
            icon={<FilterOutlined />}
            type={filtersOpen ? "primary" : "default"}
            aria-expanded={filtersOpen}
            aria-controls="auftrag-filter-panel"
            onClick={() => setFiltersOpen(open => !open)}
          >
            {t("Filter")}
          </Button>
        </Badge>
      </div>
      {filtersOpen && (
        <div className="auftrag-filter-panel" id="auftrag-filter-panel">
          {sourceSelect}
          {clientSelect}
          {sortSelect}
          {activeFilterCount > 0 && (
            <Button type="link" onClick={resetFilters}>
              {t("Reset")}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const desktopFilters = (
    <Flex justify="space-between" wrap="wrap" gap={8} className="mb-3">
      {tabs}
      <Space wrap>
        {searchBar}
        {sourceSelect}
        {clientSelect}
        {sortSelect}
      </Space>
    </Flex>
  );

  return (
    <div className="auftragssuche-page">
      <div className="auftrag-header">
        <div className="auftrag-status">{ready && renderStatusLine()}</div>
        <div className="auftrag-header-actions">
          <Button icon={<SettingOutlined />} onClick={() => setProfileOpen(true)}>
            {t("Search profile")}
          </Button>
          {ready && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={status?.running}
              onClick={handleSync}
            >
              {t("Search now")}
            </Button>
          )}
        </div>
      </div>

      {profile && !ready ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>
                  {t("Find public tenders and private requests automatically")}
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="auftrag-empty-text">
                  {t(
                    "Tell us your trades and where you work. We check German public tenders and private requests every day, rate how well each one fits you, and prepare the offer."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            <Button type="primary" onClick={() => setProfileOpen(true)}>
              {t("Set up search profile")}
            </Button>
          </Empty>
        </Card>
      ) : (
        ready && (
          <>
            {isMobile ? mobileFilters : desktopFilters}

            {tab === "new" && (withoutLocationCount > 0 || filters.withoutLocation) && (
              <Checkbox
                className="mb-3"
                checked={filters.withoutLocation}
                onChange={e => updateFilter({ withoutLocation: e.target.checked })}
              >
                {t("Also show tenders without a location ({{count}})", {
                  count: withoutLocationCount
                })}
              </Checkbox>
            )}

            {tab === "new" && sourceStates.some(s => s.lastError) && (
              <Alert
                type="warning"
                showIcon
                className="mb-3"
                message={t("A source could not be checked. The list may be incomplete.")}
              />
            )}

            <Card className="auftrag-results" styles={{ body: { padding: 0 } }}>
              <List
                loading={loading}
                dataSource={data}
                renderItem={renderItem}
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        tab === "new"
                          ? status?.running
                            ? t("Searching the sources…")
                            : t(
                                "No new matches. Widen the radius or add trades in the search profile."
                              )
                          : t("Nothing here yet.")
                      }
                    />
                  )
                }}
              />
            </Card>
            {count > PAGE_SIZE && (
              <Flex justify={isMobile ? "center" : "end"} className="mt-3">
                <Pagination
                  simple={isMobile}
                  current={pageNo}
                  pageSize={PAGE_SIZE}
                  total={count}
                  onChange={setPageNo}
                  showSizeChanger={false}
                />
              </Flex>
            )}
          </>
        )
      )}

      <SearchProfileModal
        open={profileOpen}
        profile={profile}
        meta={meta}
        onClose={() => setProfileOpen(false)}
        onSaved={handleProfileSaved}
      />
      <LeadDetailDrawer
        leadKey={detailKey}
        open={!!detailKey}
        onClose={() => setDetailKey(null)}
        onChanged={loadLeads}
        onPrepareOffer={lead => setOfferLead(lead)}
      />
      <PrepareOfferModal
        lead={offerLead}
        open={!!offerLead}
        onClose={() => setOfferLead(null)}
        onCreated={() => {
          loadLeads();
        }}
      />
    </div>
  );
};

export default Auftragssuche;
